import { format } from "date-fns";
import * as React from "react";
import { Col } from "react-bootstrap";

import { saveMonthlyFormFilter } from "../../actions/dashboardAction";
import { showPlantListModal } from "../../actions/modalActions";
import { IFilters, IHistory, IMonthlyForms } from "../../interfaces";
import {
  getFlaggedForms,
  getIndividualFormSummary,
} from "../../services/dashboardService";
import { splitDate } from "../../utils/generalUtils";
import { BarChart } from "../reusableComponents/ChartComponents/BarChart";
import { ChartFooterButtons } from "../reusableComponents/ChartComponents/ChartFooterButtons";
import { CustomToolTip } from "../reusableComponents/CustomToolTip";
import { RDatePicker } from "../reusableComponents/DatePicker";
import { RegionFilter } from "../reusableComponents/RegionFilter";
import { MonthPicker } from "../reusableComponents/MonthPicker";
import { monthsAndYears } from "../../constants/generals";

export interface IMonthlyChartProps {
  onFilter: Function;
  monthlyForms: IMonthlyForms;
  filterData: {
    monthlyFilter: IFilters;
    lineChartFilter: IFilters;
  };
  monthlyFormData: any;
  monthlyFlaggedForms: any;
  history: IHistory;
  instance: any;
  resetFilters: boolean;
  showFilter: boolean;
  monthlyFlaggedFormCount: string;
  activeDate: Date;
  activeRegion: string;
  quarterlyFilterDate?: string;
  dailyFilterDate?: string;
}

export interface IMonthlyChartState {
  regionFilter: string;
  lineChartRegion: string;
  queryString: string;
  monthlyFilterValue: Date;
  lineChartFilter: Date;
}

export class MonthlyChart extends React.PureComponent<
  IMonthlyChartProps,
  IMonthlyChartState
> {
  constructor(props: IMonthlyChartProps) {
    super(props);
    this.state = {
      monthlyFilterValue: props.activeDate,
      lineChartFilter: new Date(),
      regionFilter: "",
      queryString: "monthly",
      lineChartRegion: props.filterData.lineChartFilter.region || "",
    };
  }

  componentWillReceiveProps(nextProps: IMonthlyChartProps) {
    const queryString = `monthly?date=${format(
      nextProps.activeDate,
      "YYYY-MM-DD"
    )}&&region=${nextProps.activeRegion}`;
    if (this.props.activeDate !== nextProps.activeDate) {
      this.setState({
        queryString,
        monthlyFilterValue: nextProps.activeDate,
      });
    }
    if (this.props.activeRegion !== nextProps.activeRegion) {
      this.setState({
        queryString,
        regionFilter: nextProps.activeRegion,
      });
    }
  }

  renderCustomToolTip = (e) => {
    return <CustomToolTip eventObject={e} />;
  };

  handleDateFilter = async (date) => {
    this.props.onFilter();
    let defaultSplit = date.split("-");
    let defaultValue;
    if (defaultSplit && defaultSplit[1].length == 3) {
      monthsAndYears.map((data) => {
        if (data.label === defaultSplit[1]) {
          defaultValue = `${data.value}-${defaultSplit[0]}-${defaultSplit[2]}`;
        }
      });
    }
    const { mm, dd, yyyy } = splitDate(format(defaultValue, "YYYY-MM-DD"));
    date = defaultValue;
    //const { mm, dd, yyyy } = splitDate(format(date, "YYYY-MM-DD"));

    await getIndividualFormSummary(
      mm,
      dd,
      yyyy,
      "Monthly",
      this.state.regionFilter,
      this.props.instance
    );
    await getFlaggedForms(
      "quarterlyForm",
      mm,
      dd,
      yyyy,
      this.state.regionFilter,
      this
    );
    saveMonthlyFormFilter(date, this.state.regionFilter);
    const queryString = `monthly?date=${format(date, "YYYY-MM-DD")}&&region=${
      this.props.activeRegion
    }`;
    /*tslint:disable-next-line*/
    const dashboardQueryString = `/dashboard?date=${format(
      this.props.activeDate,
      "YYYY-MM-DD"
    )}&&region=${this.props.activeRegion}&&monthly=${format(
      date,
      "YYYY-MM-DD"
    )}&&daily=${format(
      this.props.dailyFilterDate,
      "YYYY-MM-DD"
    )}&&quarterly=${format(this.props.quarterlyFilterDate, "YYYY-MM-DD")}`;
    this.setState({
      monthlyFilterValue: date,
      queryString,
    });
    this.props.history.push(dashboardQueryString);
    this.props.instance.setState({
      isLoading: false,
    });
  };

  handleRegionFilter = async (formType, region) => {
    this.props.onFilter();
    const { mm, dd, yyyy } = splitDate(
      format(this.state.monthlyFilterValue, "YYYY-MM-DD")
    );
    await getIndividualFormSummary(mm, dd, yyyy, "Monthly", region);
    await getFlaggedForms("quarterlyForm", mm, dd, yyyy, region, this);
    this.setState({
      regionFilter: region,
    });
    this.props.instance.setState({
      isLoading: false,
    });
  };

  handleClickOnMonthlyForms = (data, barColor: string) => {
    const { monthlyForms } = this.props;
    if (barColor.toUpperCase() === "#DBDBDB") {
      showPlantListModal(
        "dashboard",
        monthlyForms[`Monthly${data.name}`]["InCompletePlantIds"],
        "Incomplete Plants",
        `Monthly${data.name}`
      );
    } else {
      showPlantListModal(
        "dashboard",
        monthlyForms[`Monthly${data.name}`]["CompletePlantIds"],
        "Complete Plants",
        `Monthly${data.name}`
      );
    }
  };
  render() {
    return (
      <Col
        sm={4}
        xs={12}
        style={styles.centerChartStyle}
        className="center-chart"
      >
        <ChartTitle>Monthly Form Summary</ChartTitle>
        {this.props.showFilter
          ? [
              <Filter
                key="Date"
                value={this.state.monthlyFilterValue}
                onApply={(val) => this.handleDateFilter(val)}
                type="month"
                children={
                  <RegionFilter
                    selectedValue={this.state.regionFilter}
                    key="region"
                    onChange={this.handleRegionFilter}
                  />
                }
              />,
            ]
          : ""}
        <BarChart
          customTooltip={(e) => this.renderCustomToolTip(e)}
          onClick={(e, barColor) => {
            this.handleClickOnMonthlyForms(e, barColor);
          }}
          barColors={["#63AB66", "#DBDBDB"]}
          dataKey={["Complete", "Incomplete"]}
          data={this.props.monthlyFormData}
        />
        <ChartFooterButtons
          flaggedForms={this.props.monthlyFlaggedFormCount}
          history={this.props.history}
          label="Monthly"
          redirectTo={this.state.queryString}
          onClickFlaggedForms={() =>
            showPlantListModal(
              "flagged-forms",
              this.props.monthlyFlaggedForms,
              "Monthly Flagged Forms",
              "monthly"
            )
          }
        />
      </Col>
    );
  }
}

const Filter = ({ type, onApply, value, children }) => {
  return (
    <div className="filter-container">
      <MonthPicker
        defaultValue={format(new Date(value).toString(), "MM-DD-YYYY")}
        years={[2013, 2015, 2016]}
        onClick={(e) => onApply(e.toString())}
        popover={true}
      />
      {/* <RDatePicker
            value={new Date(value)}
            onChange={e => onApply(e.toString())} /> */}
    </div>
  );
};

const ChartTitle = ({ children }) => {
  return (
    <div className="chart-title" style={{ fontSize: "20px" }}>
      {children}
    </div>
  );
};
const styles = {
  pieChartStyle: {
    float: "none",
    display: "inline-block",
    padding: "50px 0 130px 20px",
  },
  centerChartStyle: {
    /* float: 'none', */
    display: "inline-block",
    padding: "50px 0 130px 20px",
  },
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Dashboard/MonthlyChart.tsx